/* eslint-disable fp/no-let */
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer'
import { RefObject, useEffect } from 'react'
import { useSyncedRef } from './useSyncedRef'

/**
 * @file useResizeObserver
 * @see https://github.com/react-hookz/web/blob/master/src/useResizeObserver/useResizeObserver.ts
 */

export type UseResizeObserverCallback = (entry: ResizeObserverEntry) => void

interface ResizeObserverSingleton {
    observer: ResizeObserver
    subscribe: (target: Element, callback: UseResizeObserverCallback) => void
    unsubscribe: (target: Element, callback: UseResizeObserverCallback) => void
}

let observerSingleton: ResizeObserverSingleton

/**
 * Returns a __ResizeObserver__ instance that can be used to observe multiple
 * elements. This is a _singleton_ that will be shared across all instances of
 * __useResizeObserver__.
 */
const getResizeObserver = (): ResizeObserverSingleton | undefined => {
    if (typeof window === 'undefined') return undefined

    if (observerSingleton) {
        return observerSingleton
    }

    const callbacks = new Map<Element, Set<UseResizeObserverCallback>>()

    const ResizeObserver = window?.ResizeObserver || ResizeObserverPolyfill
    const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        entries.forEach((entry) =>
            callbacks.get(entry.target)?.forEach((cb) => cb(entry)),
        )
    })

    observerSingleton = {
        observer,
        subscribe: (target, callback) => {
            let cbs = callbacks.get(target)

            if (!cbs) {
                // if target has no observers yet - register it
                cbs = new Set<UseResizeObserverCallback>()
                callbacks.set(target, cbs)
                observer.observe(target)
            }

            // as Set is duplicate-safe - simply add callback on each call
            cbs.add(callback)
        },
        unsubscribe: (target, callback) => {
            const cbs = callbacks.get(target)

            // else branch should never occur in case of normal execution
            // because callbacks map is hidden in closure - it is impossible to
            // simulate situation with non-existent `cbs` Set
            if (cbs) {
                // remove current observer
                cbs.delete(callback)

                if (!cbs.size) {
                    // no observers left - unregister target completely
                    callbacks.delete(target)
                    observer.unobserve(target)
                }
            }
        },
    }

    return observerSingleton
}

const isRef = <T extends Element>(
    target: RefObject<T> | T,
): target is RefObject<T> =>
    typeof target === 'object' && target !== null && 'current' in target

/**
 * Uses a singleton __ResizeObserver__ to observe the `target` element,
 * invoking `callback` on each resize. `callback` does not need to be memoized.
 */
export const useResizeObserver = <T extends Element>(
    target: RefObject<T> | T,
    callback: UseResizeObserverCallback,
) => {
    const node = isRef(target) ? target.current : target
    const cb = useSyncedRef(callback)
    const ro = getResizeObserver()

    useEffect(() => {
        if (!ro) return

        // This secondary target resolve is required in case we receive a ref
        // object which contains null during the render stage (almost always),
        // but is already populated with the target DOM element during the effect stage.
        const node = isRef(target) ? target.current : target
        if (!node) return

        // as unsubscription in internals of our ResizeObserver abstraction can
        // happen a bit later than effect cleanup invocation - we need a marker,
        // that this handler should not be invoked anymore
        let subscribed = true

        const handler: UseResizeObserverCallback = (...args) => {
            if (subscribed) {
                cb.current(...args)
            }
        }

        ro.subscribe(node, handler)

        return () => {
            subscribed = false
            ro.unsubscribe(node, handler)
        }
    }, [node, ro])
}
